import React, { useState } from 'react'
import './Select.css'

/**
 * Reusable Select Component
 *
 * A customizable dropdown select component with label and error handling
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Select label
 * @param {string} props.value - Selected value
 * @param {Function} props.onChange - Change handler
 * @param {Array} props.options - Array of options [{value, label}]
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.error - Error message
 * @param {boolean} props.required - Required field
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref for React Hook Form compatibility
 */
const Select = React.forwardRef(
	(
		{
			label,
			value,
			onChange,
			options = [],
			placeholder = 'Select an option',
			error,
			required = false,
			disabled = false,
			className = '',
			id,
			...props
		},
		ref
	) => {
		const [isFocused, setIsFocused] = useState(false)

		const selectId = id || `select-${label?.replace(/\s+/g, '-').toLowerCase()}`
		const baseClass = 'select'
		const wrapperClasses = [
			`${baseClass}-wrapper`,
			error && `${baseClass}-wrapper--error`,
			isFocused && `${baseClass}-wrapper--focused`,
			disabled && `${baseClass}-wrapper--disabled`,
			className,
		]
			.filter(Boolean)
			.join(' ')

		return (
			<div className={wrapperClasses}>
				{label && (
					<label htmlFor={selectId} className={`${baseClass}__label`}>
						{label}
						{required && <span className={`${baseClass}__required`}>*</span>}
					</label>
				)}
				<select
					id={selectId}
					className={`${baseClass}__field`}
					value={value}
					onChange={onChange}
					disabled={disabled}
					required={required}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					ref={ref}
					{...props}
				>
					{placeholder && (
						<option value="" disabled>
							{placeholder}
						</option>
					)}
					{options.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
				{error && <span className={`${baseClass}__error`}>{error}</span>}
			</div>
		)
	}
)

Select.displayName = 'Select'

export default Select
